import React, { useState } from 'react';

const Card = ({
  title = "Title",
  showTitle = true,
  showPrimaryButton = true,
  showSecondaryButton = true,
  showMenuButton = true,
  showSwapComponent1 = true,
  showSwapComponent2 = true,
  showSwapComponent3 = true,
  showDescription = true,
  primaryButtonComponent = null,
  secondaryButtonText = "Button",
  description = "Optional description can be added here",
  // Custom content props
  swapContent1 = null,
  swapContent2 = null,
  swapContent3 = null,
  swapLabel1 = "Swap component 1",
  swapLabel2 = "Swap component 2",
  swapLabel3 = "Swap component 3",
  width = "w-full",
  className = "",
  onPrimaryClick = () => console.log("Primary button clicked"),
  onSecondaryClick = () => console.log("Secondary button clicked"),
  onMenuClick = () => console.log("Menu button clicked"),
}) => {
  const [activeSwap, setActiveSwap] = useState(1);

  // Check if any swap component has content
  const hasSwapContent1 = Boolean(swapContent1);
  const hasSwapContent2 = Boolean(swapContent2);
  const hasSwapContent3 = Boolean(swapContent3);

  // Only show tabs if there's more than one visible swap component with either content or empty but visible
  const visibleSwapComponents = [
    showSwapComponent1,
    showSwapComponent2,
    showSwapComponent3
  ].filter(Boolean).length;

  // Determine if swap tabs section should be shown at all
  const showSwapTabs = visibleSwapComponents > 1;

  const renderSwapComponent = (number, isVisible, label, hasContent) => {
    if (!isVisible) return null;

    return (
      <div
        className={`self-stretch p-1 ${activeSwap === number ? 'bg-blue-50' : 'bg-sky-100'} rounded-sm outline outline-1 outline-offset-[-1px] outline-blue-600 inline-flex justify-start items-center gap-2 cursor-pointer`}
        onClick={() => setActiveSwap(number)}
      >
        <div className="size-4 relative bg-blend-multiply bg-white overflow-hidden">
          <div className="size-2.5 left-[2.98px] top-[3px] absolute bg-blue-600" />
        </div>
        <div className="flex-1 justify-start text-blue-600 text-xs font-['Arial'] leading-none">
          {label}
        </div>
      </div>
    );
  };

  // Modified renderActiveContent function to only handle swap content
  const renderActiveSwapContent = () => {
    // Get the appropriate content based on active swap
    let activeContent = null;
    switch (activeSwap) {
      case 1:
        activeContent = swapContent1;
        break;
      case 2:
        activeContent = swapContent2;
        break;
      case 3:
        activeContent = swapContent3;
        break;
      default:
        activeContent = null;
    }

    // If we have custom content for the active swap, return it
    if (activeContent) {
      return activeContent;
    }

    // Default placeholder content if no content is available
    return (
      <div className="self-stretch text-gray-500 text-sm">
        No content available for this section.
      </div>
    );
  };

  // Default primary button if no custom component is provided
  const DefaultPrimaryButton = () => (
    <div data-type="Default" className="px-4 py-2 bg-blue-600 rounded-lg flex justify-center items-center gap-2 cursor-pointer">
      <div className="text-center justify-start text-white text-sm font-normal font-['Arial'] leading-normal">
        Button
      </div>
    </div>
  );

  return (
    <div
      data-action-type="Buttons"
      data-alignement="Left"
      data-labels="No"
      data-swap-position="Middle"
      data-variant="Widget card"
      className={`${width} max-w-full rounded-xl inline-flex flex-col justify-start items-start ${className}`}
    >
      {/* Header */}
      {showTitle && (
        <div className="self-stretch px-6 py-3 bg-white rounded-tl-xl rounded-tr-xl outline outline-1 outline-offset-[-1px] outline-neutral-200 inline-flex justify-start items-center gap-2">

          <div className="flex-1 justify-start text-neutral-900 text-xl font-bold font-['Arial'] capitalize leading-loose">
            {title}
          </div>


          <div className="flex justify-start items-center gap-2">
            {showPrimaryButton && (
              <div
                data-size="Compact"
                data-state="Default"
                data-type="Primary"
                data-varaints="Default"
                className="flex justify-start items-start"
                onClick={onPrimaryClick}
              >
                {primaryButtonComponent || <DefaultPrimaryButton />}
              </div>
            )}

            {showSecondaryButton && (
              <div
                data-size="Compact"
                data-state="Default"
                data-type="Secondary"
                data-varaints="Default"
                className="flex justify-start items-start"
                onClick={onSecondaryClick}
              >
                <div data-type="Default" className="px-4 py-2 rounded-lg outline outline-1 outline-offset-[-1px] outline-blue-600 flex justify-center items-center gap-2 cursor-pointer">
                  <div className="text-center justify-start text-blue-600 text-sm font-normal font-['Arial'] leading-normal">
                    {secondaryButtonText}
                  </div>
                </div>
              </div>
            )}


            {showMenuButton && (
              <div
                className="flex justify-start items-start cursor-pointer"
                onClick={onMenuClick}
              >
                <div className="p-1 rounded-lg flex justify-center items-center gap-2">
                  <div className="size-6 relative overflow-hidden">
                    <div className="w-[3px] h-3 left-[10.50px] top-[5.50px] absolute bg-blue-600" />
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Content */}
      <div className="self-stretch p-6 bg-white rounded-bl-xl rounded-br-xl outline outline-1 outline-offset-[-1px] outline-neutral-200 flex flex-col justify-start items-start gap-6">
        {/* Only show swap tabs if we should show multiple components */}
        {showSwapTabs && (
          <div className="self-stretch flex flex-wrap gap-2">
            {renderSwapComponent(1, showSwapComponent1, swapLabel1, hasSwapContent1)}
            {renderSwapComponent(2, showSwapComponent2, swapLabel2, hasSwapContent2)}
            {renderSwapComponent(3, showSwapComponent3, swapLabel3, hasSwapContent3)}
          </div>
        )}

        {/* Content Area - Split into two sections: Swap Content and Description */}
        <div className="self-stretch">
          {/* Swap Component Content */}
          {(showSwapComponent1 || showSwapComponent2 || showSwapComponent3) && (
            <div className="self-stretch mb-4">
              {renderActiveSwapContent()}
            </div>
          )}
          
          {/* Description Content - Now independent from swap content */}
          {showDescription && (
            <div className="self-stretch justify-start text-neutral-900 text-base font-['Arial'] leading-normal">
              {description}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Card;